/* eslint-disable consistent-return */
/* eslint-disable react/button-has-type */
import React, { forwardRef, useImperativeHandle } from 'react';

import { useMutation } from '@tanstack/react-query';

import FormContainer from '../../../../FormContainer';

import useBorrowersForm from '../../../../../../forms/loan/borrower';
import BorrowerService from '../../../../../../services/BorrowerService';
import LoanService from '../../../../../../services/LoanService';
import { useUiContext } from '../../../../../../context/UiContext';
import Button from '../../../../../Button';

const hasPrimary = (borrowers) => {
  return borrowers.some((br) => br.position === 'primary');
};

const BorrowerForm = forwardRef(
  (
    {
      loanId,
      loan,
      borrowers,
      //  switchToNextSection,
      borrower,
      //  hasBorrowers,
      noSubmit,
      setBorrowers,
      isVisible,
      index,
      readOnly,
      refetchLoan,
    },
    ref
  ) => {
    const { showToast, showDialogModal } = useUiContext();

    let borrowersForm;

    const { mutate: createBorrower } = useMutation({
      mutationFn: async (values) => {
        const borrowerPosition = !hasPrimary(borrowers)
          ? 'primary'
          : 'secondary';

        // If values.id exists, it means that we are attaching an existing borrower to the loan
        // Otherwise, we are creating a new borrower
        if (values.id) {
          const hasChanged = borrowersForm.formik.dirty;

          // If the borrower form has changed, we need to update the borrower first
          if (hasChanged) {
            await BorrowerService.updateBorrower({
              formValues: {
                ...values,
              },
              borrowerId: values.id,
            });
          }

          return LoanService.updateLoan({
            loanId,
            formValues: {
              ...loan,
              borrowers: [
                ...loan.borrowers,
                { borrower_id: values.id, position: borrowerPosition },
              ],
            },
          });
        }
        return BorrowerService.createBorrower({
          formValues: {
            ...values,
            loan_position: borrowerPosition,
          },
          loanId,
        });
      },
      onSuccess: () => {
        showToast({
          text: 'Success',
          type: 'success',
        });
        refetchLoan();
      },
      onError: (error) => {
        showToast({
          text: 'Something went wrong',
          type: 'error',
        });
        console.log(error);
      },
    });

    const { mutate: updateBorrower } = useMutation({
      mutationFn: (values) => {
        return BorrowerService.updateBorrower({
          formValues: {
            ...values,
            loan_position:
              index === 0 && !hasPrimary(borrowers) ? 'primary' : 'secondary',
            user_id: values.user_id || null,
            qb_customer_id: values.qb_customer_id || null,
          },
          borrowerId: borrower._borrower.id,
        });
      },
      onSuccess: () => {
        showToast({
          text: 'Success',
          type: 'success',
        });

        refetchLoan();
      },
      onError: (error) => {
        showToast({
          text: 'Something went wrong',
          type: 'error',
        });
        console.log(error);
      },
    });

    const serverBorrower =
      borrower.borrowerFromSearch || (readOnly ? borrower : borrower._borrower);

    borrowersForm = useBorrowersForm(
      borrower._borrower ? updateBorrower : createBorrower,
      serverBorrower
        ? {
            ...serverBorrower,
            position: serverBorrower.position,
            entity_type:
              serverBorrower?.TIN_type === 'EIN' ? 'Entity' : 'Individual',
          }
        : null,
      borrower.borrower_id,
      readOnly
    );

    useImperativeHandle(ref, () => ({
      submitForm: borrowersForm.formik.submitForm,
    }));

    const { mutate: deleteBorrower } = useMutation({
      // eslint-disable-next-line no-shadow
      mutationFn: async ({ borrower }) => {
        if (borrower.borrower_id.toString().startsWith('0.')) {
          setBorrowers((prevBorrowers) => {
            return prevBorrowers.filter(
              (br) => br.borrower_id !== borrower.borrower_id
            );
          });
        } else {
          await LoanService.updateLoan({
            loanId,
            formValues: {
              ...loan,
              borrowers: loan.borrowers.filter(
                (br) => br.borrower_id !== borrower.borrower_id
              ),
            },
          });
        }
      },
      onSuccess: () => {
        showToast({
          text: 'Success',
          type: 'success',
        });
        refetchLoan();
      },
      onError: (error) => {
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
        console.log(error);
      },
    });

    return (
      <div className="relative p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        {!readOnly && (
          <button
            onClick={() => {
              showDialogModal({
                title: 'Delete borrower',
                message: 'Are you sure you want to delete this borrower?',
                onConfirm: () => {
                  deleteBorrower({
                    borrower,
                  });
                },
              });
            }}
            type="button"
            className="absolute right-4 block rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <span className="sr-only">Back</span>
            <svg width="32" height="32" viewBox="0 0 32 32">
              <path
                className="fill-current"
                d="M15.95 14.536l4.242-4.243a1 1 0 111.415 1.414l-4.243 4.243 4.243 4.242a1 1 0 11-1.415 1.415l-4.242-4.243-4.243 4.243a1 1 0 01-1.414-1.415l4.243-4.242-4.243-4.243a1 1 0 011.414-1.414l4.243 4.243z"
              />
            </svg>
          </button>
        )}
        <FormContainer
          groupSizeOffset={index * 16}
          isVisible={isVisible}
          form={borrowersForm}
          noSubmit={noSubmit}
          readOnly={readOnly}
          noBillComId={readOnly}
        />
        {!readOnly && (
          <div className="flex mt-8">
            <Button
              type="submit"
              onClick={borrowersForm.formik.handleSubmit}
              isSubmitting={borrowersForm.formik.isSubmitting}
              disabled={
                !borrowersForm.formik.dirty && !borrower.borrowerFromSearch
              }
            >
              Submit
            </Button>
          </div>
        )}
      </div>
    );
  }
);

export default BorrowerForm;
